import FileSaver from "file-saver";
import { flatten } from "flat";
import { camelCase, difference, intersection, isEmpty } from "lodash-es";
import Papa from "papaparse";
import type React from "react";
import { useState } from "react";
import DownloadButtonContent from "../../../core/buttons/content/DownloadButtonContent";
import { Button, type ButtonProps } from "../../Theme/button";

interface CSVInnerButtonProps {
  // one of download or data must be specified
  download?: () => Promise<any[]>; // optional promise which returns the data that can be converted in to a csv
  data?: any[]; // optional data which can be converted into a csv.

  filename?: string;
  columnOrder?: string[];
  sortColumns?: boolean;
  disabled?: boolean;
  camelCaseColumns?: boolean;
}

export const unparse = (
  data: Array<any>,
  columnOrder?: string[],
  sortColumns = true,
  camelCaseColumns = false,
): any => {
  const dataFields: Set<string> = new Set();
  const objects: Array<any> = data.map((d) => {
    const o: any = flatten(d, {
      transformKey: camelCaseColumns ? camelCase : undefined,
    });

    // TODO: fix this for each? maybe add some tests?
    // biome-ignore lint/complexity/noForEach: dont have time to fix this right now
    Object.keys(o).forEach((k) => {
      if (
        o[k] &&
        isEmpty(o[k]) &&
        o[k] !== null &&
        o[k].constructor === Object
      ) {
        delete o[k];
      } else {
        dataFields.add(k);
      }
    });
    return o;
  });
  let columns: string[];
  if (columnOrder) {
    const orderedColumns = intersection(columnOrder, [...dataFields]);
    const remainder = difference([...dataFields], orderedColumns);

    columns = [...orderedColumns, ...remainder.sort()];
  } else {
    if (sortColumns) {
      columns = [...dataFields].sort();
    } else {
      columns = [...dataFields];
    }
  }

  return Papa.unparse(objects, { columns: columns });
};

const saveDataAsFile = (
  data: Array<any>,
  filename?: string,
  columnOrder?: string[],
  sortColumns = true,
  camelCaseColumns = true,
): void => {
  FileSaver.saveAs(
    new Blob([unparse(data, columnOrder, sortColumns, camelCaseColumns)], {
      type: "text/csv;charset=utf-8",
    }),
    `${filename ? filename : "export"}.csv`,
  );
};

export type CSVExportButtonProps = CSVInnerButtonProps &
  Omit<ButtonProps, "outline" | "bright" | "plain">;

const CSVExportButton = (props: CSVExportButtonProps): React.ReactElement => {
  const {
    download,
    data,
    filename,
    columnOrder,
    sortColumns = true,
    camelCaseColumns = false,
    color = "brandLight",
    disabled = false,
    ...rest
  } = props;

  const [loading, setLoading] = useState(false);

  const downloadClickHandler = (): void => {
    setLoading(true);
    if (download) {
      download()
        .then((data: any[]) => {
          saveDataAsFile(
            data,
            filename,
            columnOrder,
            sortColumns,
            camelCaseColumns,
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (data) {
      saveDataAsFile(
        data,
        filename,
        columnOrder,
        sortColumns,
        camelCaseColumns,
      );
      setLoading(false);
    } else {
      setLoading(false);
      throw new Error("Data is not available");
    }
  };

  return (
    <Button
      color={color}
      onClick={downloadClickHandler}
      disabled={loading || disabled}
      {...rest}
    >
      <DownloadButtonContent loading={loading} />
    </Button>
  );
};

export default CSVExportButton;
